// ✅ FINALIZED: src/components/Staff/LeaveRequestForm.jsx

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { fetchEmergencyStatus, isFeatureLocked } from '@/utils/emergencyLock';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import { useNavigate } from 'react-router-dom';

const LeaveRequestForm = () => {
  const [leaveType, setLeaveType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState('');
  const [isLocked, setIsLocked] = useState(false);
  const [loading, setLoading] = useState(false);

  const { canRequestLeave, user } = useUserPermissions();
  const navigate = useNavigate();

  useEffect(() => {
    if (!canRequestLeave || !user?.staff_id) {
      navigate('/unauthorized');
    }

    const checkStatus = async () => {
      const status = await fetchEmergencyStatus();
      setIsLocked(isFeatureLocked(status, ['offline', 'recovery']));
    };

    checkStatus();
  }, [canRequestLeave, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');

    if (!leaveType || !startDate || !endDate || !reason) {
      setMessage('⚠️ All fields are required.');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post('/solo-mdsh-api/staffing/leave-requests', {
        staff_id: user.staff_id,
        leave_type: leaveType.toLowerCase(),
        start_date: startDate,
        end_date: endDate,
        reason
      });

      setMessage(res.data.message || '✅ Leave request submitted!');
      setLeaveType('');
      setStartDate('');
      setEndDate('');
      setReason('');
    } catch (err) {
      console.error('Leave request error:', err);
      setMessage('❌ Failed to submit leave request.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-neutral-800 p-6 rounded-2xl shadow-xl text-white max-w-lg mx-auto">
      <h2 className="text-xl font-semibold mb-4">📅 Request Leave</h2>

      {isLocked ? (
        <div className="bg-red-600 text-white p-3 rounded-lg font-medium text-center">
          🚫 Leave requests are currently disabled due to Emergency Mode.
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <select
            value={leaveType}
            onChange={(e) => setLeaveType(e.target.value)}
            className="bg-neutral-700 p-2 rounded w-full"
          >
            <option value="">Select Leave Type</option>
            <option value="holiday">Annual Leave</option>
            <option value="sick">Sick Leave</option>
            <option value="leo">LEO Day</option>
          </select>

          <div className="flex gap-2">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="bg-neutral-700 p-2 rounded w-full"
            />
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="bg-neutral-700 p-2 rounded w-full"
            />
          </div>

          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason for leave..."
            className="bg-neutral-700 p-2 rounded w-full"
          ></textarea>

          <button
            type="submit"
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 p-2 px-6 rounded-xl font-semibold w-full disabled:opacity-50"
          >
            {loading ? 'Submitting...' : 'Submit Request'}
          </button>
        </form>
      )}

      {message && (
        <div className="mt-4 text-yellow-400 font-medium text-center">{message}</div>
      )}
    </div>
  );
};

export default LeaveRequestForm;
